import { Component, OnInit } from '@angular/core';
import { RelocationSandbox } from 'app/core/sandboxes/relocation.sandbox';
import { RecurrenceService } from 'app/core/services/recurrence.service';
import { ScrollService } from 'app/core/services/scroll.service';
import { MaterialTypes } from 'app/core/store/contract-details/contract-details.interface';
import { TemplateState } from 'app/core/store/template/template.interface';
import { distinctUntilChanged, filter, takeWhile } from 'rxjs/operators';
import { fadeInAnimation } from '../../../animations';
import { Place } from '../../../core/interfaces/place.interface';
import { Definitions } from '../../../core/store/definitions/definitions.interface';
import { OrderMaterial, RelocationDetail, } from '../../../core/store/edit-florder-detail/florder-detail.interface';
import * as relocationActions from '../../../core/store/edit-relocation-detail/relocation-detail.actions';
import { EditRelocationDetail, LoadingDates } from '../../../core/store/edit-relocation-detail/relocation-detail.interface';
import { FlorderEditContainer } from '../../florder/florder-edit-container/florder-edit.container';

@Component({
  selector: 'app-relocation-edit',
  templateUrl: './relocation-edit.container.html',
  styleUrls: ['../../florder/florder-edit-container/florder-edit.container.scss'],
  animations: [fadeInAnimation],
})
export class RelocationEditContainer extends FlorderEditContainer<OrderMaterial> implements OnInit {
  relocationDetail: RelocationDetail;
  allowedLocations: Place[];
  availableLoadingDates: LoadingDates;
  materialTypes: MaterialTypes;

  constructor(private recurrenceService: RecurrenceService, private sandbox: RelocationSandbox, private scrollService: ScrollService) {
    super('relocations');

    this.store
      .select<EditRelocationDetail>(state => state.editRelocationDetail)
      .pipe(
        takeWhile(() => !this.destroyed),
        distinctUntilChanged(),
        filter(val => val.mode.saved),
      )
      .subscribe(value => {
        this.openCreateFeedbackDialog({
          isSuccess: value.mode.saveSuccess,
          etmOrderNumber: value.relocationDetail.etmOrderNumber,
          salesOrderNumber: value.relocationDetail.salesOrderNumber,
          otherEtmOrderNumbers: value.relocationDetail.otherEtmOrderNumbers,
        });
      });

    this.store
      .select('template')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((templateState: TemplateState) => (this.templateState = templateState));

    this.store
      .select('editRelocationDetail')
      .pipe(
        takeWhile(() => !this.destroyed),
        filter((editRelocationDetail: EditRelocationDetail) => !!editRelocationDetail.relocationDetail)
      )
      .subscribe((editRelocationDetail: EditRelocationDetail) => {
        this.mode = editRelocationDetail.mode;
        const materialEdit = !!(editRelocationDetail.relocationDetail && editRelocationDetail.relocationDetail.materials && editRelocationDetail.mode.material);
        const materialExists = materialEdit && editRelocationDetail.relocationDetail.materials.find(m => m.internalId === editRelocationDetail.mode.material.internalId) != null;
        this.panelStatus = this.getPanelStatusses(materialEdit, materialExists);
        this.relocationDetail = editRelocationDetail.relocationDetail;
        this.availableLoadingDates = editRelocationDetail.availableLoadingDates;
        this.materialTypes = editRelocationDetail.materialsState.data;
      });

    this.store
      .select('definitions')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((definitions: Definitions) => {
        this.allowedLocations = definitions.places.filter(place => place.relocationAllowed);
      });
  }

  downloadDocument(documentId: string, extension: string): void {
    throw new Error('Unsupported operation exception for relocations'); // not in scope for relocations
  }

  ngOnInit(): void {
    this.scrollService.scrollToElement('top');
  }

  editDeliveryMethodClicked() {
    this.store.dispatch(new relocationActions.RelocationDetailEditDeliveryMethodAction());
  }

  editDetailClicked() {
    this.store.dispatch(new relocationActions.RelocationDetailCreateEditOrderDetailAction());
  }

  editPlanningClicked(): void {
    this.sandbox.editPlanning();
  }

  setDeliveryMethod(form): void {
    // First load materials (into state)
    this.sandbox.loadMaterialsFromIdToId(form.from.id, form.to.id).subscribe(() => {
      // Then go to edit details
      this.store.dispatch(new relocationActions.RelocationDetailCreateSetDeliveryMethodAction({ deliveryMethod: form }));
      this.scrollService.scrollToElement('step-details');
    });
  }

  removeMaterial(material): void {
    this.store.dispatch(new relocationActions.RelocationDetailCreateRemoveMaterialAction(material));
  }

  editMaterial(material) {
    this.store.dispatch(new relocationActions.RelocationDetailCreateStartEditMaterialAction(material));
  }

  getMaterials(): (OrderMaterial)[] {
    return this.relocationDetail.materials;
  }

  saveMaterial(material) {
    if (this.checkMaterialIsUnique(material)) {
      this.store.dispatch(
        new relocationActions.RelocationDetailCreateSaveMaterialStartAction({
          material,
          showPopup: true,
        })
      );
    }
  }

  saveDetails() {
    this.sandbox.saveDetails();
  }

  savePlanning(form) {
    this.store.dispatch(new relocationActions.RelocationDetailSavePlanningAction(form));
    this.scrollService.scrollToElement('submit-button', 200);
  }

  submitClicked() {
    this.create();
  }

  get disableButtons(): boolean {
    return (this.templateState && this.templateState.saving) || (this.mode && (this.mode.saving || this.mode.state === 'recurrence-dates'));
  }

  protected openCreateFeedbackDialog({ isSuccess, etmOrderNumber, salesOrderNumber, otherEtmOrderNumbers = null, showCopyButton = false }): Promise<void> {
    this.azureMonitoringService.logEvent('RelocationEditContainer in openCreateFeedbackDialog');
    try {
      return super.openCreateFeedbackDialog({
        isSuccess,
        etmOrderNumber,
        salesOrderNumber,
        messageType: '',
        ccrNumber: null,
        incoterm: null,
        showDownloadDocumentButton: false,
        showCreateButton: true,
        moduleRoute: 'relocation',
        moduleRouteOverview: 'relocations/refresh',
        createAction: new relocationActions.RelocationDetailCreateStartAction({ date: null }),
        showCopyButton: true,
        copyFunction: this.sandbox.copyRelocation.bind(this.sandbox),
        otherEtmOrderNumbers
      });
    } catch (e) {
      this.azureMonitoringService.logEvent('RelocationEditContainer ERROR in openCreateFeedbackDialog');
      this.azureMonitoringService.logException(e);
      throw e;
    }
  }

  create() {
    if (this.mode.createTemplate || this.mode.editTemplate) {
      this.sandbox.submitCreateTemplate();
    } else {
      this.sandbox.submitCreateRelocation();
    }
  }

  resetCreatePage() {
    const isTemplate = this.mode.createTemplate || this.mode.editTemplate;
    this.sandbox.resetCreatePage(isTemplate);
  }

  copyRelocation(): void {
    this.sandbox.copyRelocation(this.relocationDetail.etmOrderNumber);
  }
}
